import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Alert, AlertDescription } from '../../ui/alert';
import { Separator } from '../../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../ui/select';
import {
  X,
  History,
  Filter
} from 'lucide-react';
import LocationStockCard from './LocationStockCard';
import { sortLocations, getFreshnessStats } from '../../../utils/inventoryHelpers';

/**
 * LocationSelectorDialog Component
 * Modal dialog for selecting inventory location with filtering and sorting
 */
function LocationSelectorDialog({
  open,
  onClose,
  product,
  onSelect,
  onViewHistory,
  selectedLocationName = null
}) {
  const [selected, setSelected] = useState(selectedLocationName);
  const [showOnlyWithStock, setShowOnlyWithStock] = useState(true);
  const [sortBy, setSortBy] = useState('freshness');

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelected(selectedLocationName);
    }
  }, [open, selectedLocationName]);

  // Filter and sort locations
  const filteredLocations = useMemo(() => {
    if (!product || !product.locations) return [];

    let filtered = product.locations;

    if (showOnlyWithStock) {
      filtered = filtered.filter(loc => loc.available_quantity > 0);
    }

    return sortLocations(filtered, sortBy);
  }, [product, showOnlyWithStock, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!product || !product.locations) {
      return { fresh: 0, recent: 0, stale: 0, unknown: 0, total: 0 };
    }
    return getFreshnessStats(product.locations);
  }, [product]);

  const totalStock = useMemo(() => {
    return product?.locations?.reduce(
      (sum, loc) => sum + (loc.available_quantity || 0),
      0
    ) || 0;
  }, [product]);

  const handleLocationSelect = (location) => {
    setSelected(location.location);
  };

  const handleConfirm = () => {
    if (!product) return;

    const selectedLocation = product.locations?.find(
      loc => loc.location === selected
    );
    if (selectedLocation && onSelect) {
      onSelect(selectedLocation);
      onClose();
    }
  };

  const handleViewFullHistory = () => {
    if (onViewHistory && product) {
      onViewHistory(product.product_id || product.product_code, null);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <DialogTitle>选择取货位置</DialogTitle>
              <DialogDescription>{product.product_name}</DialogDescription>
              <div className="flex gap-2 mt-2">
                <Badge>总库存: {totalStock}{product.unit || '支'}</Badge>
                <Badge variant="outline">{product.locations?.length || 0} 个位置</Badge>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" onClick={handleViewFullHistory}>
                <History className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Freshness Statistics Banner */}
        {stats.total > 0 && (
          <div className="flex gap-2 flex-wrap px-1">
            {stats.fresh > 0 && (
              <Badge className="bg-success/20 text-success">{stats.fresh} 最新</Badge>
            )}
            {stats.recent > 0 && (
              <Badge className="bg-warning/20 text-warning">{stats.recent} 较新</Badge>
            )}
            {stats.stale > 0 && (
              <Badge className="bg-destructive/20 text-destructive">{stats.stale} 过时</Badge>
            )}
          </div>
        )}

        <Separator />

        {/* Filters and Sorting */}
        <div className="flex gap-4 items-center flex-wrap py-2 bg-muted px-2 rounded">
          <Filter className="h-4 w-4 text-muted-foreground" />

          <div className="flex items-center gap-2">
            <Switch
              id="stock-filter"
              checked={showOnlyWithStock}
              onCheckedChange={setShowOnlyWithStock}
            />
            <Label htmlFor="stock-filter" className="text-sm">仅显示有库存位置</Label>
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="排序方式" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="freshness">按数据新鲜度</SelectItem>
              <SelectItem value="quantity">按库存数量</SelectItem>
              <SelectItem value="location">按位置名称</SelectItem>
              <SelectItem value="activity">按最近活动</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Location Cards */}
        <div className="flex-1 overflow-y-auto py-2">
          {filteredLocations.length === 0 ? (
            <Alert className="mt-4">
              <AlertDescription>
                {showOnlyWithStock
                  ? '没有符合条件的库存位置。尝试取消"仅显示有库存位置"筛选。'
                  : '此产品暂无库存位置信息。'
                }
              </AlertDescription>
            </Alert>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {filteredLocations.map((location, index) => (
                <LocationStockCard
                  key={index}
                  location={location}
                  product={product}
                  onSelect={handleLocationSelect}
                  onViewHistory={onViewHistory}
                  selected={selected === location.location}
                  showSourceInfo={true}
                />
              ))}
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button onClick={handleConfirm} disabled={!selected}>
            确认选择
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LocationSelectorDialog;
